import { put, takeLatest } from 'redux-saga/effects';
import { PayloadAction } from 'redux-starter-kit';
import {
  Requests,
  Resources,
  ResolverResponse,
  Mutations,
} from '@erecruit/types';
import {
  getResourceApi,
  PositionSearchRequestWithDataPref,
  PositionSearchCountRequestWithDataPref,
  closeActionStatus,
} from '@erecruit/modules';
import { RouteComponentProps } from 'react-router-dom';

import {
  searchPositionsSuccess,
  searchPositionsFail,
  searchPositionShiftTypesSuccess,
  searchPositionShiftTypesFail,
  addShiftsSuccess,
  addShiftsError,
} from './reducer';
import { withStatusInfo } from '../../ActionStatus';

function* fetchPositions({
  payload,
}: PayloadAction<Resources.PositionSearchParams>) {
  try {
    const filter = payload.filter || {};
    const newFilter = { ...filter };
    newFilter.supportsShifts = true;
    const params = { ...payload, filter: newFilter };

    const responses = yield getResourceApi().getResources([
      new PositionSearchRequestWithDataPref(params),
      new PositionSearchCountRequestWithDataPref({
        filter: newFilter,
      }),
    ]) as Promise<ResolverResponse[]>;

    const positions: Resources.PositionSearch[] = responses[0].data;
    const totalCount: number = responses[1].data[0].value;
    yield put(
      searchPositionsSuccess({ positions: positions, totalCount: totalCount }),
    );
  } catch (e) {
    yield put(searchPositionsFail(e.message));
  }
}

function* fetchPositionShiftTypes({ payload }: PayloadAction<number>) {
  try {
    const response = yield getResourceApi().getResources([
      new Requests.PositionShiftTypesRequest({
        positionIds: [payload],
      }),
    ]);

    const positionShiftTypes: Resources.PositionShiftTypes =
      response[0].data[0];
    const shiftTypesForPosition = positionShiftTypes.shiftTypes;
    yield put(searchPositionShiftTypesSuccess(shiftTypesForPosition));
  } catch (e) {
    yield put(searchPositionShiftTypesFail(e.message));
  }
}

function* addShiftsHandler({
  payload,
}: PayloadAction<Mutations.NewShiftsParams & { positionName: string }>) {
  yield getResourceApi().createMutationRequest(
    new Requests.NewShiftsRequest(payload),
  );
  yield put(addShiftsSuccess());
}

function* addShiftsErrorHandler() {
  yield put(addShiftsError());
}

function* watchForRequestData() {
  yield takeLatest(
    'addNewShift/searchPositionShiftTypes',
    fetchPositionShiftTypes,
  );
  yield takeLatest('addNewShift/searchPositions', fetchPositions);
  yield takeLatest(
    'addNewShift/addShifts',
    withStatusInfo({
      successGeneratorFn: addShiftsHandler,
      errorGeneratorFn: addShiftsErrorHandler,
      autocloseMs: 2000,
      getSuccessMessage: ({ payload }) => ({
        id: messageIds.vms.newShiftWizard.addShift.successMessage,
        values: {
          count: String(payload.shifts.length),
          position: payload.positionName,
        },
      }),
      getLoadingMessage: () => ({
        id: messageIds.vms.newShiftWizard.addShift.loadingMessage,
      }),
      getHeader: ({ payload }) => ({
        title: {
          id: messageIds.vms.newShiftWizard.title,
        },
        subtitle: {
          id: messageIds.vms.general.placeholder,
          values: { placeholder: payload.positionName },
        },
      }),
      getCustomSuccessAction: () => ({
        display: {
          text: { id: messageIds.vms.newShiftWizard.addNew },
          icon: 'plus',
        },
        action: (history: Pick<RouteComponentProps, 'history'>['history']) => {
          history.push('/addNewShift');
          closeActionStatus();
        },
      }),
    }),
  );
}

export default [watchForRequestData()];
